import { addressFor, type AddressWords } from './address';
import type { Kin, KinPack, KinStep, Sex } from './types';

/**
 * Relationship words for Indian languages (PRD 7.2.2). Unlike English or Spanish, the word depends on the
 * side of the family (kaka for a father's brother, mama for a mother's), on who is older (tau and chacha in
 * Hindi), and on whose spouse links you (devar is a husband's younger brother, sala a wife's brother). The
 * rules here read all of that from the `Kin` path; each language only supplies its words and grammar, once
 * in its own script and once in English letters.
 *
 * Distant relatives are named the way families actually address them: a parent's cousin is called by the
 * same word as a parent's sibling, a grandparent's sibling by the grandparent word for that side. The
 * "how you're related" line always spells out the exact path.
 */

/** A male and a female form. With no gender on record both are shown: "Kaka/Foi". */
export interface MF {
  m: string;
  f: string;
}
/** Words that change with age compared to the person before them in the path. */
export interface ByAge {
  elder: string;
  younger: string;
  any: string;
}

export type UncleKind = 'FB' | 'FZ' | 'MB' | 'MZ';

export interface IndicWords {
  you: string;
  relative: string;
  spouse: MF; // husband / wife
  parent: MF;
  child: MF;
  brother: ByAge;
  sister: ByAge;
  /** Grandparents (and their brothers and sisters) on the father's side, and on the mother's. */
  grandF: MF;
  grandM: MF;
  /** Great-grandparents on each side. Further up: `ancestor`. */
  greatF: MF;
  greatM: MF;
  ancestor: string;
  /** A son's children and a daughter's children (also a nephew's and niece's, as families say). */
  grandchildViaSon: MF;
  grandchildViaDaughter: MF;
  greatGrandchild: MF;
  descendant: string;
  /** A parent's brothers and sisters (and a parent's cousins), and their spouses. */
  uncle: Record<UncleKind, { elder: string; any: string }>;
  uncleSpouse: Record<UncleKind, { elder: string; any: string }>;
  /** First cousins, named by which uncle or aunt is their parent. */
  cousin: (kind: UncleKind, sex: Sex) => string;
  distantCousin: MF;
  /** A brother's children, a sister's children. */
  nephewViaBrother: MF;
  nephewViaSister: MF;
  parentInLaw: MF;
  /** A spouse's grandparents: on the spouse's father's side, and (when the language has its own word) the mother's. */
  grandparentInLaw: MF;
  grandparentInLawM?: MF;
  /** A spouse's uncles and aunts: "Kaka Sasra", "Mausi Saas". */
  spouseUncle: (kind: UncleKind, sex: Sex) => string;
  husbandBrother: ByAge;
  husbandSister: string;
  wifeBrother: string;
  wifeSister: string;
  husbandBrotherWife: ByAge;
  husbandSisterHusband: string;
  wifeSisterHusband: string;
  wifeBrotherWife: string;
  brotherWife: string;
  /** Compared with you: Hindi says jija for an elder sister's husband and behnoi for a younger one's. */
  sisterHusband: ByAge;
  /** m: son-in-law, f: daughter-in-law (also for grandchildren's, nephews' and nieces' spouses). */
  childInLaw: MF;
  coParentInLaw: MF;
  stepParent: MF;
  stepChild: MF;
  stepSibling: MF;
}

/** How a language joins "your X's Y's Z" for the "how you're related" line. */
export interface IndicGrammar {
  /** The noun for one step of the path. `oblique` is its form before a possessive (Gujarati dikro → dikra). */
  noun(step: KinStep): { word: string; oblique: string; fem: boolean; honorific: boolean };
  /** "Your" before the first noun. */
  your(fem: boolean, honorific: boolean): string;
  /** The possessive before the next noun, which agrees with that noun. */
  of(nextFem: boolean, nextIsLast: boolean, nextHonorific: boolean): string;
  /** Gujarati script writes the possessive onto the noun (પપ્પાના); everything else uses a space. */
  attach: boolean;
  generation(delta: number): string;
}

/** Alternatives for an unknown gender or side, each word once ("Jadd", not "Jadd/Jadd", where both sides share it). */
export const alt = (...words: string[]) => [...new Set(words)].join('/');
export const either = (w: MF) => alt(w.m, w.f);
export const pick = (w: MF, sex: Sex) => (sex === 'f' ? w.f : sex === 'm' ? w.m : either(w));
const byAge = (w: ByAge, elder: boolean | null) => (elder === true ? w.elder : elder === false ? w.younger : w.any);

function uncleKind(side: Sex, sex: Sex): UncleKind | null {
  if (!side || !sex) return null;
  return `${side === 'm' ? 'F' : 'M'}${sex === 'm' ? 'B' : 'Z'}` as UncleKind;
}

/** A parent's brother or sister (or cousin), or a word for both when side or gender is unknown. */
function uncle(side: Sex, sex: Sex, elder: boolean | null, table: IndicWords['uncle']): string {
  const kind = uncleKind(side, sex);
  if (kind) return elder === true ? table[kind].elder : table[kind].any;
  const sides: Sex[] = side ? [side] : ['m', 'f'];
  const sexes: Sex[] = sex ? [sex] : ['m', 'f'];
  return alt(...sides.flatMap((s) => sexes.map((x) => table[uncleKind(s, x)!].any)));
}

function grandparent(w: IndicWords, side: Sex, sex: Sex, depth: number): string {
  if (depth > 3) return w.ancestor;
  const [f, m] = depth === 2 ? [w.grandF, w.grandM] : [w.greatF, w.greatM];
  if (side === 'm') return pick(f, sex);
  if (side === 'f') return pick(m, sex);
  return alt(pick(f, sex), pick(m, sex));
}

function grandchild(w: IndicWords, viaSex: Sex, sex: Sex): string {
  if (viaSex === 'm') return pick(w.grandchildViaSon, sex);
  if (viaSex === 'f') return pick(w.grandchildViaDaughter, sex);
  return alt(pick(w.grandchildViaSon, sex), pick(w.grandchildViaDaughter, sex));
}

function nephew(w: IndicWords, viaSex: Sex, sex: Sex): string {
  if (viaSex === 'm') return pick(w.nephewViaBrother, sex);
  if (viaSex === 'f') return pick(w.nephewViaSister, sex);
  return alt(pick(w.nephewViaBrother, sex), pick(w.nephewViaSister, sex));
}

type Shape = NonNullable<Kin['shape']>;

/** Blood relatives along `steps` (which start at the person the relationship is counted from). */
function blood(w: IndicWords, steps: KinStep[], shape: Shape, sex: Sex): string {
  if ('up' in shape) {
    if (shape.up === 1) return pick(w.parent, sex);
    return grandparent(w, steps[0]!.sex, sex, shape.up);
  }
  if ('down' in shape) {
    if (shape.down === 1) return pick(w.child, sex);
    if (shape.down === 2) return grandchild(w, steps[0]!.sex, sex);
    return shape.down === 3 ? pick(w.greatGrandchild, sex) : w.descendant;
  }
  const { a, b } = shape;
  const g = b - a; // generations below the viewer (negative: above)
  const side = steps[0]!.sex;
  if (a === 1 && b === 1) return sex === 'f' ? byAge(w.sister, steps[0]!.elder) : sex === 'm' ? byAge(w.brother, steps[0]!.elder) : `${w.brother.any}/${w.sister.any}`;
  if (g === -1) return uncle(side, sex, a === 2 ? steps[1]!.elder : null, w.uncle);
  if (g <= -2) return grandparent(w, side, sex, -g);
  if (g === 0) {
    if (a === 2) {
      const kind = uncleKind(side, steps[1]!.sex);
      return kind ? w.cousin(kind, sex) : pick(w.distantCousin, sex);
    }
    return pick(w.distantCousin, sex);
  }
  // Below: the relative of the viewer's generation in their line decides nephew or grandchild words.
  if (g === 1) return nephew(w, steps[2 * a - 2]!.sex, sex);
  if (g === 2) return grandchild(w, steps[2 * a - 1]!.sex, sex);
  return w.descendant;
}

/** Relatives of my spouse (via 'mine'). `rest` starts at the spouse's first step. */
function spouseSide(w: IndicWords, spouse: KinStep, rest: KinStep[], shape: Shape, sex: Sex): string {
  const grandInLaw = () => pick(rest[0]!.sex === 'f' && w.grandparentInLawM ? w.grandparentInLawM : w.grandparentInLaw, sex);
  if ('up' in shape) {
    if (shape.up === 1) return pick(w.parentInLaw, sex);
    return shape.up === 2 ? grandInLaw() : w.ancestor;
  }
  if ('down' in shape) return shape.down === 1 ? pick(w.stepChild, sex) : blood(w, rest, shape, sex);
  const { a, b } = shape;
  const g = b - a;
  if (a === 1 && b === 1) {
    if (spouse.sex === 'm') return sex === 'f' ? w.husbandSister : sex === 'm' ? byAge(w.husbandBrother, rest[0]!.elder) : `${w.husbandBrother.any}/${w.husbandSister}`;
    if (spouse.sex === 'f') return sex === 'f' ? w.wifeSister : sex === 'm' ? w.wifeBrother : `${w.wifeBrother}/${w.wifeSister}`;
    return w.relative;
  }
  if (g === -1) {
    const kind = uncleKind(rest[0]!.sex, sex);
    return kind ? w.spouseUncle(kind, sex) : w.relative;
  }
  if (g === -2) return grandInLaw();
  if (g >= 1) return blood(w, rest, shape, sex);
  return w.relative;
}

/** Spouses of my blood relatives (via 'theirs'). `rel` is the blood path to the relative they married. */
function relativesSpouse(w: IndicWords, rel: KinStep[], shape: Shape, sex: Sex): string {
  if ('up' in shape) return shape.up === 1 ? pick(w.stepParent, sex) : grandparent(w, rel[0]!.sex, sex, shape.up);
  if ('down' in shape) return pick(w.childInLaw, sex);
  const { a, b } = shape;
  const g = b - a;
  const relative = rel[rel.length - 1]!;
  if (g === 0) {
    if (relative.sex === 'm') return w.brotherWife;
    if (relative.sex === 'f') return a === 1 ? byAge(w.sisterHusband, relative.elder) : w.sisterHusband.any;
    return `${w.brotherWife}/${w.sisterHusband.any}`;
  }
  if (g === -1) return uncle(rel[0]!.sex, relative.sex, a === 2 ? rel[1]!.elder : null, w.uncleSpouse);
  if (g <= -2) return grandparent(w, rel[0]!.sex, sex, -g);
  return pick(w.childInLaw, sex);
}

/** My spouse's sibling's spouse (via 'both'): [spouse, sibling, their spouse]. */
function coSiblingInLaw(w: IndicWords, path: KinStep[]): string {
  const [spouse, sib] = path as [KinStep, KinStep];
  if (spouse.sex === 'm' && sib.sex === 'm') return byAge(w.husbandBrotherWife, sib.elder);
  if (spouse.sex === 'm' && sib.sex === 'f') return w.husbandSisterHusband;
  if (spouse.sex === 'f' && sib.sex === 'f') return w.wifeSisterHusband;
  if (spouse.sex === 'f' && sib.sex === 'm') return w.wifeBrotherWife;
  return w.relative;
}

export function indicLabel(w: IndicWords, k: Kin): string {
  switch (k.kind) {
    case 'none':
      return '';
    case 'self':
      return w.you;
    case 'spouse':
      return pick(w.spouse, k.sex);
    case 'coParentInLaw':
      return pick(w.coParentInLaw, k.sex);
    case 'stepSibling':
      return pick(w.stepSibling, k.sex);
    case 'relative':
      return w.relative;
    case 'blood':
      return blood(w, k.path, k.shape!, k.sex);
    case 'marriage':
      if (k.via === 'mine') return spouseSide(w, k.path[0]!, k.path.slice(1), k.shape!, k.sex);
      if (k.via === 'theirs') return relativesSpouse(w, k.path.slice(0, -1), k.shape!, k.sex);
      return coSiblingInLaw(w, k.path);
  }
}

/** "Your father's brother's son", step by step, with the possessive agreeing with each next noun. */
export function indicExplain(g: IndicGrammar, k: Kin, capitalise: boolean): string {
  if (k.path.length < 2) return '';
  const nouns = k.path.map((s) => g.noun(s));
  const sep = g.attach ? '' : ' ';
  let out = `${g.your(nouns[0]!.fem, nouns[0]!.honorific)} `;
  nouns.forEach((n, i) => {
    const last = i === nouns.length - 1;
    out += last ? n.word : n.oblique;
    if (!last) {
      const next = nouns[i + 1]!;
      out += `${sep}${g.of(next.fem, i + 1 === nouns.length - 1, next.honorific)} `;
    }
  });
  return capitalise ? out.charAt(0).toUpperCase() + out.slice(1) : out;
}

/** Builds a word set from a language's words, grammar and forms of address. */
export function indicPack(meta: Pick<KinPack, 'id' | 'name' | 'lang'>, w: IndicWords, g: IndicGrammar, latin: boolean, addr: AddressWords): KinPack {
  return {
    ...meta,
    dir: 'ltr',
    status: 'beta',
    label: (k) => indicLabel(w, k),
    generation: (delta) => g.generation(delta),
    explain: (k) => indicExplain(g, k, latin),
    address: (k, name) => addressFor(addr, k, name),
  };
}

/** Grandparent forms by side: [father's father, father's mother, mother's father, mother's mother], plain and great. */
export function grandAddress(plain: [string[], string[], string[], string[]], great: [string[], string[], string[], string[]]): AddressWords['grandparent'] {
  return (side, sex, isGreat) => {
    const t = isGreat ? great : plain;
    return t[(side === 'f' ? 2 : 0) + (sex === 'f' ? 1 : 0)]!;
  };
}

/** The noun for a step, from male, female and either forms. */
export function stepNoun(step: KinStep, forms: Record<KinStep['rel'], { m: N; f: N }>): N & { fem: boolean } {
  const pair = forms[step.rel];
  if (step.sex === 'f') return { ...pair.f, fem: true };
  if (step.sex === 'm') return { ...pair.m, fem: false };
  return { word: `${pair.m.word}/${pair.f.word}`, oblique: `${pair.m.oblique}/${pair.f.oblique}`, honorific: pair.m.honorific, fem: false };
}
export interface N {
  word: string;
  oblique: string;
  honorific: boolean;
}
